import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Play, X, ChevronLeft, ChevronRight, ArrowLeft, MessageCircle, Tv } from 'lucide-react'

// ─── Video Data ────────────────────────────────────────────────
const categories = [
  {
    key: 'greffe',
    label: 'Greffes de Cheveux',
    videos: [
      { id: 'greffe-esthetique', title: 'Greffe de Cheveux — Esthétique', duration: '0:32', src: '/media-bentv/greffe/greffe-esthetique.mp4', poster: '/media-bentv/greffe/greffe-esthetique.webp' },
      { id: 'david-intro', title: 'David Benaim — Expert Capillaire', duration: '0:30', src: '/media-bentv/greffe/david-intro.mp4', poster: '/media-bentv/greffe/david-intro.webp' },
      { id: 'francois-vacanti', title: 'François — Témoignage Patient', duration: '0:50', src: '/media-bentv/greffe/francois-vacanti.mp4', poster: '/media-bentv/greffe/francois-vacanti.webp' },
      { id: 'elad-temoignage', title: 'Elad — Mon Parcours', duration: '0:57', src: '/media-bentv/greffe/elad-temoignage.mp4', poster: '/media-bentv/greffe/elad-temoignage.webp' },
      { id: 'bastien-temoignage', title: 'Bastien — Avant/Après', duration: '1:11', src: '/media-bentv/greffe/bastien-temoignage.mp4', poster: '/media-bentv/greffe/bastien-temoignage.webp' },
      { id: 'vincent-interview', title: 'Vincent — Interview', duration: '1:11', src: '/media-bentv/greffe/vincent-interview.mp4', poster: '/media-bentv/greffe/vincent-interview.webp' },
      { id: 'ep02-greffe', title: 'Épisode 2 — La Greffe', duration: '1:14', src: '/media-bentv/greffe/ep02-greffe.mp4', poster: '/media-bentv/greffe/ep02-greffe.webp' },
      { id: 'greffe-dov', title: 'Greffe de Cheveux — Dov', duration: '1:11', src: '/media-bentv/greffe/greffe-dov.mp4', poster: '/media-bentv/greffe/greffe-dov.webp' },
      { id: 'ep02-suivi-greffe', title: 'Épisode 2 — Suivi de Greffe', duration: '1:41', src: '/media-bentv/greffe/ep02-suivi-greffe.mp4', poster: '/media-bentv/greffe/ep02-suivi-greffe.webp' },
    ],
  },
  {
    key: 'epilation',
    label: 'Épilation Laser',
    videos: [
      { id: 'epilation-laser', title: 'Épilation Laser — Le Guide', duration: '0:22', src: '/media-bentv/epilation/epilation-laser.mp4', poster: '/media-bentv/epilation/epilation-laser.webp' },
    ],
  },
  {
    key: 'detatouage',
    label: 'Détatouage',
    videos: [
      { id: 'detatouage-session', title: 'Séance de Détatouage', duration: '0:40', src: '/media-bentv/detatouage/detatouage-session.mp4', poster: '/media-bentv/detatouage/detatouage-session.webp' },
      { id: 'detatouage-avant-apres', title: 'Avant / Après', duration: '0:14', src: '/media-bentv/detatouage/detatouage-avant-apres.mp4', poster: '/media-bentv/detatouage/detatouage-avant-apres.webp' },
      { id: 'detatouage-resultat', title: 'Résultat Final', duration: '0:03', src: '/media-bentv/detatouage/detatouage-resultat.mp4', poster: '/media-bentv/detatouage/detatouage-resultat.webp' },
      { id: 'detatouage-process', title: 'Le Processus', duration: '0:06', src: '/media-bentv/detatouage/detatouage-process.mp4', poster: '/media-bentv/detatouage/detatouage-process.webp' },
    ],
  },
]

// ─── Video Card ────────────────────────────────────────────────
const VideoCard = ({ video, onClick }) => {
  const [hovered, setHovered] = useState(false)

  return (
    <div
      className="bentv-card"
      onClick={() => onClick(video)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        flex: '0 0 auto',
        width: '200px',
        scrollSnapAlign: 'start',
        cursor: 'pointer',
        position: 'relative',
        borderRadius: '12px',
        overflow: 'hidden',
        transform: hovered ? 'scale(1.05)' : 'scale(1)',
        transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), box-shadow 0.3s ease',
        boxShadow: hovered
          ? '0 12px 40px rgba(0, 0, 0, 0.5)'
          : '0 4px 16px rgba(0, 0, 0, 0.3)',
        zIndex: hovered ? 2 : 1,
      }}
    >
      {/* 9:16 aspect ratio container */}
      <div style={{
        position: 'relative',
        width: '100%',
        paddingBottom: '177.78%', /* 16/9 * 100 */
        background: '#1a1a2e',
      }}>
        <img
          src={video.poster}
          alt={video.title}
          loading="lazy"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'opacity 0.3s ease',
          }}
          onError={(e) => {
            e.target.style.display = 'none'
          }}
        />

        {/* Play button overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: hovered ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)',
          transition: 'background 0.3s ease',
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: hovered ? 1 : 0.7,
            transform: hovered ? 'scale(1.1)' : 'scale(1)',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}>
            <Play size={22} color="#2F2F84" fill="#2F2F84" style={{ marginLeft: '2px' }} />
          </div>
        </div>

        {/* Duration badge */}
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          background: 'rgba(0, 0, 0, 0.75)',
          color: '#fff',
          padding: '3px 8px',
          borderRadius: '6px',
          fontSize: '0.75rem',
          fontWeight: '600',
          fontFamily: 'Inter, sans-serif',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        }}>
          {video.duration}
        </div>

        {/* Title overlay at bottom */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '40px 12px 12px',
          background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)',
        }}>
          <p style={{
            color: '#fff',
            fontSize: '0.85rem',
            fontWeight: '600',
            fontFamily: 'Inter, sans-serif',
            lineHeight: '1.3',
            margin: 0,
            textShadow: '0 1px 3px rgba(0,0,0,0.5)',
          }}>
            {video.title}
          </p>
        </div>
      </div>
    </div>
  )
}

// ─── Category Row (Netflix-style horizontal scroll) ────────────
const CategoryRow = ({ category, onVideoClick }) => {
  const scrollRef = useRef(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(true)
  const [isHovered, setIsHovered] = useState(false)

  const updateArrows = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setShowLeftArrow(el.scrollLeft > 10)
    setShowRightArrow(el.scrollLeft < el.scrollWidth - el.clientWidth - 10)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    updateArrows()
    el.addEventListener('scroll', updateArrows, { passive: true })
    window.addEventListener('resize', updateArrows)
    return () => {
      el.removeEventListener('scroll', updateArrows)
      window.removeEventListener('resize', updateArrows)
    }
  }, [updateArrows])

  const scroll = (direction) => {
    const el = scrollRef.current
    if (!el) return
    const scrollAmount = el.clientWidth * 0.75
    el.scrollBy({ left: direction === 'right' ? scrollAmount : -scrollAmount, behavior: 'smooth' })
  }

  if (category.videos.length === 0) return null

  return (
    <div
      style={{ marginBottom: '48px', position: 'relative' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <h3 style={{
        color: '#fff',
        fontSize: '1.4rem',
        fontWeight: '700',
        fontFamily: "'Playfair Display', serif",
        marginBottom: '16px',
        paddingLeft: '24px',
      }}>
        {category.label}
        <span style={{
          marginLeft: '12px',
          fontSize: '0.85rem',
          color: 'rgba(255,255,255,0.4)',
          fontFamily: 'Inter, sans-serif',
          fontWeight: '400',
        }}>
          {category.videos.length} {category.videos.length > 1 ? 'vidéos' : 'vidéo'}
        </span>
      </h3>

      <div style={{ position: 'relative' }}>
        {/* Left scroll arrow */}
        {showLeftArrow && (
          <button
            onClick={() => scroll('left')}
            className="bentv-scroll-arrow bentv-scroll-arrow-left"
            style={{
              opacity: isHovered ? 1 : 0,
            }}
            aria-label="Défiler vers la gauche"
          >
            <ChevronLeft size={28} color="#fff" />
          </button>
        )}

        {/* Right scroll arrow */}
        {showRightArrow && (
          <button
            onClick={() => scroll('right')}
            className="bentv-scroll-arrow bentv-scroll-arrow-right"
            style={{
              opacity: isHovered ? 1 : 0,
            }}
            aria-label="Défiler vers la droite"
          >
            <ChevronRight size={28} color="#fff" />
          </button>
        )}

        {/* Scrollable row */}
        <div
          ref={scrollRef}
          style={{
            display: 'flex',
            gap: '16px',
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            scrollBehavior: 'smooth',
            paddingLeft: '24px',
            paddingRight: '24px',
            paddingBottom: '8px',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
          className="bentv-scroll-row"
        >
          {category.videos.map((video) => (
            <VideoCard key={video.id} video={video} onClick={onVideoClick} />
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Video Player Modal ────────────────────────────────────────
const VideoPlayerModal = ({ video, onClose }) => {
  const videoRef = useRef(null)

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [onClose])

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      if (videoRef.current) videoRef.current.pause()
      onClose()
    }
  }

  const handleClose = () => {
    if (videoRef.current) videoRef.current.pause()
    onClose()
  }

  return (
    <div
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(0, 0, 0, 0.92)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        animation: 'bentvFadeIn 0.25s ease',
      }}
    >
      {/* Close button */}
      <button
        onClick={handleClose}
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          zIndex: 10001,
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          border: '2px solid rgba(255,255,255,0.3)',
          background: 'rgba(0, 0, 0, 0.6)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.15)'
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)'
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'
        }}
        aria-label="Fermer"
      >
        <X size={22} />
      </button>

      {/* Video container — vertical 9:16 */}
      <div style={{
        position: 'relative',
        maxHeight: '90vh',
        aspectRatio: '9 / 16',
        width: 'auto',
        maxWidth: '90vw',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        animation: 'bentvScaleIn 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      }}>
        <video
          ref={videoRef}
          src={video.src}
          poster={video.poster}
          controls
          autoPlay
          playsInline
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            background: '#000',
          }}
        />
      </div>

      {/* Video title below */}
      <div style={{
        position: 'absolute',
        bottom: '24px',
        left: '50%',
        transform: 'translateX(-50%)',
        textAlign: 'center',
        maxWidth: '80vw',
      }}>
        <p style={{
          color: '#fff',
          fontSize: '1.1rem',
          fontWeight: '600',
          fontFamily: 'Inter, sans-serif',
          textShadow: '0 2px 8px rgba(0,0,0,0.5)',
          margin: 0,
        }}>
          {video.title}
        </p>
      </div>
    </div>
  )
}

// ─── Main BenTV Component ──────────────────────────────────────
const BenTV = () => {
  const [activeVideo, setActiveVideo] = useState(null)

  const handleVideoClick = useCallback((video) => {
    setActiveVideo(video)
  }, [])

  const handleCloseModal = useCallback(() => {
    setActiveVideo(null)
  }, [])

  const handleBenChat = () => {
    if (typeof window.openBenChat === 'function') {
      window.openBenChat('ben-tv')
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0a1a',
      position: 'relative',
    }}>
      {/* Injected styles for scrollbar hiding, animations, media queries */}
      <style>{`
        .bentv-scroll-row::-webkit-scrollbar {
          display: none;
        }
        .bentv-scroll-arrow {
          position: absolute;
          top: 0;
          bottom: 8px;
          width: 48px;
          z-index: 3;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: opacity 0.3s ease;
          padding: 0;
        }
        .bentv-scroll-arrow-left {
          left: 0;
          background: linear-gradient(to right, rgba(10, 10, 26, 0.95) 0%, transparent 100%);
          border-radius: 0;
        }
        .bentv-scroll-arrow-right {
          right: 0;
          background: linear-gradient(to left, rgba(10, 10, 26, 0.95) 0%, transparent 100%);
          border-radius: 0;
        }
        @keyframes bentvFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes bentvScaleIn {
          from { transform: scale(0.92); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes bentvPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(139, 111, 71, 0.4); }
          50% { box-shadow: 0 0 0 10px rgba(139, 111, 71, 0); }
        }
        @keyframes bentvGlow {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.7; }
        }
        /* Mobile adjustments */
        @media (max-width: 768px) {
          .bentv-card {
            width: 160px !important;
          }
          .bentv-hero-title {
            font-size: 2.5rem !important;
          }
          .bentv-hero-subtitle {
            font-size: 1rem !important;
          }
          .bentv-hero-inner {
            padding: 60px 20px 40px !important;
            flex-direction: column !important;
            text-align: center !important;
          }
          .bentv-hero-host {
            width: 160px !important;
            height: 220px !important;
            border-radius: 12px !important;
          }
          .bentv-hero-host img {
            content: url('/media-bentv/ben-tv-host-vertical.webp');
          }
          .bentv-back-link {
            top: 16px !important;
            left: 16px !important;
          }
          .bentv-ben-cta {
            bottom: 20px !important;
            right: 20px !important;
            width: 52px !important;
            height: 52px !important;
          }
          .bentv-ben-cta span {
            display: none !important;
          }
        }
        @media (max-width: 480px) {
          .bentv-card {
            width: 140px !important;
          }
        }
      `}</style>

      {/* ── Back link ── */}
      <a
        href="/"
        className="bentv-back-link"
        style={{
          position: 'fixed',
          top: '24px',
          left: '24px',
          zIndex: 100,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: 'rgba(255,255,255,0.7)',
          textDecoration: 'none',
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.9rem',
          fontWeight: '500',
          padding: '8px 16px',
          borderRadius: '24px',
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.1)',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = '#fff'
          e.currentTarget.style.background = 'rgba(47, 47, 132, 0.6)'
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'rgba(255,255,255,0.7)'
          e.currentTarget.style.background = 'rgba(0, 0, 0, 0.4)'
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'
        }}
      >
        <ArrowLeft size={18} />
        Retour
      </a>

      {/* ── Hero Banner ── */}
      <div style={{
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(160deg, #0a0a1a 0%, #1a1040 40%, #2F2F84 70%, #0a0a1a 100%)',
      }}>
        {/* Decorative glow */}
        <div style={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139, 111, 71, 0.15) 0%, transparent 70%)',
          animation: 'bentvGlow 4s ease-in-out infinite',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-30%',
          left: '-10%',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(47, 47, 132, 0.2) 0%, transparent 70%)',
          animation: 'bentvGlow 5s ease-in-out infinite',
          animationDelay: '2s',
          pointerEvents: 'none',
        }} />

        <div
          className="bentv-hero-inner"
          style={{
            position: 'relative',
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '100px 40px 60px',
            display: 'flex',
            alignItems: 'center',
            gap: '48px',
          }}
        >
          {/* Host image — wide banner crop */}
          <div className="bentv-hero-host" style={{
            flexShrink: 0,
            width: '280px',
            height: '200px',
            borderRadius: '16px',
            overflow: 'hidden',
            border: '2px solid rgba(139, 111, 71, 0.4)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 40px rgba(139, 111, 71, 0.15)',
            background: '#1a1a2e',
          }}>
            <img
              src="/media-bentv/ben-tv-host.webp"
              alt="Ben — Votre présentateur"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center top',
              }}
              onError={(e) => {
                e.target.src = '/team/ben.jpg'
              }}
            />
          </div>

          {/* Title block */}
          <div style={{ flex: 1 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '12px',
            }}>
              <Tv size={28} color="#8B6F47" />
              <span style={{
                color: '#8B6F47',
                fontSize: '0.85rem',
                fontWeight: '700',
                fontFamily: 'Inter, sans-serif',
                textTransform: 'uppercase',
                letterSpacing: '3px',
              }}>
                Exclusivité · Clinique du Grand Paris
              </span>
            </div>

            <h1
              className="bentv-hero-title"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: '3.5rem',
                fontWeight: '800',
                color: '#fff',
                lineHeight: '1.1',
                margin: '0 0 16px 0',
              }}
            >
              BEN{' '}
              <span style={{
                color: '#8B6F47',
              }}>
                TV
              </span>
            </h1>

            <p
              className="bentv-hero-subtitle"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '1.2rem',
                color: 'rgba(255, 255, 255, 0.6)',
                lineHeight: '1.5',
                maxWidth: '500px',
                margin: 0,
              }}
            >
              Votre chaîne santé & beauté. Témoignages, expertises et coulisses de la clinique, présentés par Ben.
            </p>
          </div>
        </div>

        {/* Bottom gradient fade into content */}
        <div style={{
          height: '80px',
          background: 'linear-gradient(to bottom, transparent, #0a0a1a)',
        }} />
      </div>

      {/* ── Category Rows ── */}
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        paddingBottom: '100px',
      }}>
        {categories.map((cat) => (
          <CategoryRow
            key={cat.key}
            category={cat}
            onVideoClick={handleVideoClick}
          />
        ))}
      </div>

      {/* ── Floating Ben CTA ── */}
      <button
        onClick={handleBenChat}
        className="bentv-ben-cta"
        style={{
          position: 'fixed',
          bottom: '32px',
          right: '32px',
          zIndex: 100,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '14px 24px',
          border: 'none',
          borderRadius: '50px',
          background: 'linear-gradient(135deg, #2F2F84 0%, #6466B0 100%)',
          color: '#fff',
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.95rem',
          fontWeight: '600',
          cursor: 'pointer',
          boxShadow: '0 8px 32px rgba(47, 47, 132, 0.4)',
          transition: 'all 0.3s ease',
          animation: 'bentvPulse 3s infinite',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.05)'
          e.currentTarget.style.boxShadow = '0 12px 40px rgba(47, 47, 132, 0.5)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)'
          e.currentTarget.style.boxShadow = '0 8px 32px rgba(47, 47, 132, 0.4)'
        }}
        aria-label="Demandez à Ben"
      >
        <MessageCircle size={20} />
        <span>Demandez à Ben</span>
      </button>

      {/* ── Video Player Modal ── */}
      {activeVideo && (
        <VideoPlayerModal video={activeVideo} onClose={handleCloseModal} />
      )}
    </div>
  )
}

export default BenTV
